import { useState } from 'react';
import { updateMyProfile } from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../lib/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  onClose: () => void;
}

function autoInitials(name?: string | null, email?: string | null): string {
  const src = (name ?? email ?? '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function SettingsModal({ onClose }: Props) {
  const { user, setUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [abbreviation, setAbbreviation] = useState(user?.abbreviation ?? '');
  const [defaultCatchPublic, setDefaultCatchPublic] = useState(user?.defaultCatchPublic ?? true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fallback = autoInitials(user?.displayName, user?.email);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = displayName.trim();
    const trimmedAbbr = abbreviation.trim();
    if (trimmedName.length < 1 || trimmedName.length > 40) {
      setError('Display name must be 1-40 characters.');
      return;
    }
    if (trimmedAbbr && (trimmedAbbr.length < 2 || trimmedAbbr.length > 3 || !/^[A-Za-z0-9]+$/.test(trimmedAbbr))) {
      setError('Abbreviation must be 2-3 letters or digits.');
      return;
    }

    setSubmitting(true);
    try {
      const updated = await updateMyProfile({
        displayName: trimmedName,
        abbreviation: trimmedAbbr || null,
        defaultCatchPublic,
      });
      setUser(updated);
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Display name already taken.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('Invalid input. Check your entries.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save settings.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative mt-20 w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 shadow-xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2 className="mb-5 text-lg font-bold text-slate-900 dark:text-slate-50">Settings</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Display name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              maxLength={40}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Abbreviation
            </label>
            <input
              type="text"
              maxLength={3}
              value={abbreviation}
              onChange={(e) => setAbbreviation(e.target.value.toUpperCase())}
              placeholder={fallback}
              className={`${inputClass} font-mono tracking-widest`}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              2-3 letters or digits. Leave blank to auto-derive from your name.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Make my catches public by default
              </label>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                New catches start public. You can still flip any catch individually.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={defaultCatchPublic}
              onClick={() => setDefaultCatchPublic((v) => !v)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${
                defaultCatchPublic ? 'bg-teal-600 dark:bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  defaultCatchPublic ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-teal-600 dark:bg-teal-500 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
